import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { CheckCircle2, Package, ArrowRight } from 'lucide-react'
import { gtmEvents, UserData } from '@/lib/gtm'
import { useCart } from '@/contexts/CartContext'

const ThankYou = () => {
  const [order, setOrder] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const processed = useRef(false)
  const { clearCart } = useCart()

  useEffect(() => {
    // Only run if not already processed in this component lifecycle
    if (processed.current) return

    const savedOrder = sessionStorage.getItem('lastOrder')
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder)
        setOrder(parsedOrder)
        processed.current = true

        // Post-purchase: Clear the cart state immediately
        clearCart()

        // Prepare User Data for Enhanced Conversions
        const customer = parsedOrder.customer || {}

        // Split name into first and last
        const nameParts = (customer.name || '').trim().split(' ')
        const firstName = nameParts[0] || ''
        const lastName =
          nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''

        // Format full address for GTM if parts are available
        const fullStreet = customer.number
          ? `${customer.address}, ${customer.number}${customer.neighborhood ? ` - ${customer.neighborhood}` : ''}`
          : customer.address

        const userData: UserData = {
          email: customer.email,
          phone_number: customer.phone,
          address: {
            first_name: firstName,
            last_name: lastName,
            street: fullStreet,
            city: customer.city,
            region: customer.state,
            postal_code: customer.zip,
            country: customer.country || 'BR',
          },
        }

        // Track Purchase Event with Enhanced Conversions Data
        gtmEvents.purchase(
          parsedOrder.transactionId,
          parsedOrder.total,
          parsedOrder.items.map((item: any) => ({
            item_id: item.id,
            item_name: item.name,
            price: item.price,
            quantity: item.quantity,
            item_category: item.category,
            item_variant: item.size
              ? `${item.size}/${item.color}`
              : item.item_variant,
          })),
          userData,
          0,
          parsedOrder.shipping,
        )
      } catch (e) {
        console.error('Error processing order for thank you page', e)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [clearCart])

  if (loading) {
    return null
  }

  if (!order) {
    return (
      <div className="container py-20 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <div className="bg-slate-50 p-8 rounded-lg inline-block max-w-md shadow-sm border">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Nenhum pedido recente</h2>
          <p className="text-muted-foreground mb-6">
            Não encontramos detalhes de um pedido recente. Sua sessão pode ter
            expirado.
          </p>
          <Button asChild>
            <Link to="/shop">Continuar Comprando</Link>
          </Button>
        </div>
      </div>
    )
  }

  const fullAddress = order.customer.number
    ? `${order.customer.address}, ${order.customer.number}`
    : order.customer.address

  return (
    <div className="container mx-auto px-4 py-16 text-center max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex justify-center mb-6">
        <div className="bg-green-100 p-4 rounded-full shadow-inner">
          <CheckCircle2 className="h-16 w-16 text-green-600" />
        </div>
      </div>

      <h1 className="text-4xl font-bold mb-4 tracking-tight">
        Obrigado pela sua compra!
      </h1>
      <p className="text-muted-foreground text-lg mb-8 max-w-lg mx-auto">
        Seu pedido foi confirmado com sucesso.
        {order.customer?.email &&
          ` Enviamos um e-mail de confirmação para ${order.customer.email}.`}
      </p>

      <div className="bg-white border rounded-lg p-6 text-left mb-8 shadow-sm">
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <div>
            <span className="text-sm text-muted-foreground">
              Número do Pedido
            </span>
            <p className="font-mono font-bold text-lg text-primary">
              {order.transactionId}
            </p>
          </div>
          <div className="text-right">
            <span className="text-sm text-muted-foreground">Total</span>
            <p className="font-bold text-lg">
              R$ {order.total.toFixed(2).replace('.', ',')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2 text-sm text-slate-900">
            <Package className="h-4 w-4" /> Itens do Pedido
          </h3>
          <div className="space-y-3">
            {order.items.map((item: any) => (
              <div
                key={item.id}
                className="flex justify-between text-sm items-start"
              >
                <span className="text-slate-600">
                  <span className="font-medium text-slate-900">
                    {item.quantity}x
                  </span>{' '}
                  {item.name}
                </span>
                <span className="font-medium text-slate-900">
                  R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-100 text-sm text-muted-foreground">
          <p>
            Entregar em: {fullAddress}, {order.customer.neighborhood} <br />
            {order.customer.city} - {order.customer.state}
            {order.customer.complement && (
              <>
                <br />
                {order.customer.complement}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="flex justify-center gap-4">
        <Button asChild size="lg" className="w-full sm:w-auto">
          <Link to="/shop">
            Continuar Comprando <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}

export default ThankYou
